import { Appointment } from './appointmentService';
import { AppointmentType } from './appointmentTypeService';
import { Business } from './businessService';
import { BusinessUser } from '../types/business';
import { AppointmentException } from './appointmentExceptionService';

// Database configuration
const DB_NAME = 'MindShareOfflineDB';
const DB_VERSION = 4; // Incremented for new stores and indexes

// Store names
export const STORES = {
  APPOINTMENTS: 'appointments',
  APPOINTMENT_TYPES: 'appointmentTypes',
  BUSINESSES: 'businesses',
  USERS: 'users',
  PENDING_CHANGES: 'pendingChanges',
  SYNC_METADATA: 'syncMetadata',
  APPOINTMENT_EXCEPTIONS: 'appointmentExceptions',
  CUSTOMERS: 'customers',
  JOBS: 'jobs',
  FORMS: 'forms',
  FORM_RESPONSES: 'formResponses'
};

// Appointment caching strategy:
// We store appointments from 1 month in the past to 6 months in the future
// This ensures users have access to their recent and upcoming appointments while offline
// All recurring appointments are stored regardless of date range as they may have instances in our range

// Types for pending changes
export enum ChangeType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete'
}

export interface PendingChange {
  id: string; // Unique ID for the change
  entityId: string; // ID of the entity being changed
  entityType: string; // Type of entity (appointment, appointmentType, etc.)
  changeType: ChangeType; // Type of change (create, update, delete)
  data: any; // The data to be sent to the server
  timestamp: number; // When the change was made
  endpoint: string; // API endpoint to call
  method: string; // HTTP method to use
}

export interface SyncMetadata {
  id: string; // Key for the metadata (e.g., 'lastSync')
  value: any; // Value of the metadata
}

class IndexedDbService {
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor() {
    this.initDatabase();
  }

  /**
   * Initialize the IndexedDB database
   */
  private initDatabase(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    console.log(`Initializing IndexedDB database '${DB_NAME}' with version ${DB_VERSION}`);
    
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        console.error('Error opening IndexedDB:', event);
        reject(new Error('Could not open IndexedDB'));
      };

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        this.db = db;
        console.log('IndexedDB opened successfully');
        
        // Verify the database structure after successful opening
        try {
          const requiredStores = Object.values(STORES);
          const existingStores = Array.from(db.objectStoreNames);
          
          console.log('Verifying database structure:');
          console.log('Required stores:', requiredStores);
          console.log('Existing stores:', existingStores);
          
          const missingStores = requiredStores.filter(store => !existingStores.includes(store));
          
          if (missingStores.length > 0) {
            console.error(`Database is missing required object stores: ${missingStores.join(', ')}`);
            
            // Force a database upgrade by closing and reopening with a higher version
            db.close();
            const newVersion = db.version + 1;
            console.log(`Upgrading database to version ${newVersion} to fix structure`);
            
            const upgradeRequest = indexedDB.open(DB_NAME, newVersion);
            
            upgradeRequest.onupgradeneeded = (upgradeEvent) => {
              const upgradedDb = (upgradeEvent.target as IDBOpenDBRequest).result;
              this.createObjectStores(upgradedDb);
            };
            
            upgradeRequest.onsuccess = (upgradeEvent) => {
              this.db = (upgradeEvent.target as IDBOpenDBRequest).result;
              console.log('Database upgraded and reopened successfully');
              resolve(this.db);
            };
            
            upgradeRequest.onerror = (upgradeEvent) => {
              console.error('Error upgrading database:', upgradeEvent);
              reject(new Error('Could not upgrade database'));
            };
          } else {
            console.log('Database structure verified successfully');
            resolve(db);
          }
        } catch (error) {
          console.error('Error verifying database structure:', error);
          reject(error);
        }
      };

      request.onupgradeneeded = (event) => {
        const oldVersion = event.oldVersion;
        const newVersion = event.newVersion;
        console.log(`Database upgrade needed from version ${oldVersion} to ${newVersion}`);
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = (event.target as IDBOpenDBRequest).transaction!;
        
        this.createObjectStores(db);
        
        // If upgrading from version 2 to 3, add compound index to existing appointments store
        if (oldVersion === 2 && newVersion === 3) {
          console.log('Upgrading from v2 to v3: Adding compound index to appointments store');
          if (transaction.objectStoreNames.contains(STORES.APPOINTMENTS)) {
            const appointmentsStore = transaction.objectStore(STORES.APPOINTMENTS);
            if (!appointmentsStore.indexNames.contains('businessId_startTime')) {
              console.log('Adding compound index businessId_startTime to appointments store');
              appointmentsStore.createIndex('businessId_startTime', ['businessId', 'startTime'], { unique: false });
            }
          }
        }
      };
    });

    return this.dbPromise;
  }
  
  /**
   * Create all required object stores in the database
   */
  private createObjectStores(db: IDBDatabase): void {
    // Create object stores
    if (!db.objectStoreNames.contains(STORES.APPOINTMENTS)) {
      console.log(`Creating object store: ${STORES.APPOINTMENTS}`);
      const appointmentsStore = db.createObjectStore(STORES.APPOINTMENTS, { keyPath: 'id' });
      appointmentsStore.createIndex('businessId', 'businessId', { unique: false });
      appointmentsStore.createIndex('startTime', 'startTime', { unique: false });
      appointmentsStore.createIndex('recurringAppointmentId', 'recurringAppointmentId', { unique: false });
      appointmentsStore.createIndex('businessId_startTime', ['businessId', 'startTime'], { unique: false });
    }

    if (!db.objectStoreNames.contains(STORES.APPOINTMENT_TYPES)) {
      console.log(`Creating object store: ${STORES.APPOINTMENT_TYPES}`);
      const appointmentTypesStore = db.createObjectStore(STORES.APPOINTMENT_TYPES, { keyPath: 'id' });
      appointmentTypesStore.createIndex('businessId', 'businessId', { unique: false });
    }

    if (!db.objectStoreNames.contains(STORES.BUSINESSES)) {
      console.log(`Creating object store: ${STORES.BUSINESSES}`);
      db.createObjectStore(STORES.BUSINESSES, { keyPath: 'id' });
    }

    if (!db.objectStoreNames.contains(STORES.USERS)) {
      console.log(`Creating object store: ${STORES.USERS}`);
      const usersStore = db.createObjectStore(STORES.USERS, { keyPath: 'id' });
      usersStore.createIndex('businessId', 'businessId', { unique: false });
    }

    if (!db.objectStoreNames.contains(STORES.PENDING_CHANGES)) {
      console.log(`Creating object store: ${STORES.PENDING_CHANGES}`);
      const pendingChangesStore = db.createObjectStore(STORES.PENDING_CHANGES, { keyPath: 'id' });
      pendingChangesStore.createIndex('entityType', 'entityType', { unique: false });
      pendingChangesStore.createIndex('timestamp', 'timestamp', { unique: false });
    }

    if (!db.objectStoreNames.contains(STORES.SYNC_METADATA)) {
      console.log(`Creating object store: ${STORES.SYNC_METADATA}`);
      db.createObjectStore(STORES.SYNC_METADATA, { keyPath: 'id' });
    }

    // Create the appointment exceptions store if it doesn't exist
    if (!db.objectStoreNames.contains(STORES.APPOINTMENT_EXCEPTIONS)) {
      console.log(`Creating object store: ${STORES.APPOINTMENT_EXCEPTIONS}`);
      const exceptionsStore = db.createObjectStore(STORES.APPOINTMENT_EXCEPTIONS, { keyPath: 'id' });
      exceptionsStore.createIndex('appointmentId', 'appointmentId', { unique: false });
      exceptionsStore.createIndex('exceptionDate', 'exceptionDate', { unique: false });
    }

    // Create customers store for offline customer access
    if (!db.objectStoreNames.contains(STORES.CUSTOMERS)) {
      console.log(`Creating object store: ${STORES.CUSTOMERS}`);
      const customersStore = db.createObjectStore(STORES.CUSTOMERS, { keyPath: 'id' });
      customersStore.createIndex('businessId', 'businessId', { unique: false });
    }

    // Create jobs store for offline job access
    if (!db.objectStoreNames.contains(STORES.JOBS)) {
      console.log(`Creating object store: ${STORES.JOBS}`);
      const jobsStore = db.createObjectStore(STORES.JOBS, { keyPath: 'id' });
      jobsStore.createIndex('businessId', 'businessId', { unique: false });
      jobsStore.createIndex('customerId', 'customerId', { unique: false });
    }

    // Create forms store for offline form access
    if (!db.objectStoreNames.contains(STORES.FORMS)) {
      console.log(`Creating object store: ${STORES.FORMS}`);
      const formsStore = db.createObjectStore(STORES.FORMS, { keyPath: 'id' });
      formsStore.createIndex('businessId', 'businessId', { unique: false });
      formsStore.createIndex('displayOrder', 'displayOrder', { unique: false });
    }

    // Create form responses store for offline form response access
    if (!db.objectStoreNames.contains(STORES.FORM_RESPONSES)) {
      console.log(`Creating object store: ${STORES.FORM_RESPONSES}`);
      const formResponsesStore = db.createObjectStore(STORES.FORM_RESPONSES, { keyPath: 'id' });
      formResponsesStore.createIndex('formId', 'formId', { unique: false });
      formResponsesStore.createIndex('jobId', 'jobId', { unique: false });
      formResponsesStore.createIndex('customerId', 'customerId', { unique: false });
    }
  }
  
  /**
   * Verify that the database has all required object stores
   */
  private async verifyDatabaseStructure(db: IDBDatabase): Promise<void> {
    const requiredStores = Object.values(STORES);
    const existingStores = Array.from(db.objectStoreNames);
    
    console.log('Verifying database structure:');
    console.log('Required stores:', requiredStores);
    console.log('Existing stores:', existingStores);
    
    const missingStores = requiredStores.filter(store => !existingStores.includes(store));
    
    if (missingStores.length > 0) {
      console.error(`Database is missing required object stores: ${missingStores.join(', ')}`);
      throw new Error(`Database is missing required object stores: ${missingStores.join(', ')}`);
    }
    
    return Promise.resolve();
  }

  /**
   * Get a transaction and object store
   */
  private async getStore(storeName: string, mode: IDBTransactionMode = 'readonly'): Promise<IDBObjectStore> {
    try {
      const db = await this.getDatabase();
      
      // Verify that the store exists
      if (!db.objectStoreNames.contains(storeName)) {
        console.error(`Object store '${storeName}' does not exist in the database`);
        throw new Error(`Object store '${storeName}' does not exist`);
      }
      
      const transaction = db.transaction([storeName], mode);
      
      // Add error handling for the transaction
      transaction.onerror = (event) => {
        console.error(`Transaction error for store '${storeName}':`, event);
      };
      
      return transaction.objectStore(storeName);
    } catch (error) {
      console.error(`Error getting store '${storeName}':`, error);
      
      // Try to reinitialize the database and retry
      this.db = null;
      this.dbPromise = null;
      
      // Retry once
      const db = await this.getDatabase();
      
      if (!db.objectStoreNames.contains(storeName)) {
        console.error(`Object store '${storeName}' still does not exist after reinitialization`);
        throw new Error(`Object store '${storeName}' does not exist after reinitialization`);
      }
      
      const transaction = db.transaction([storeName], mode);
      return transaction.objectStore(storeName);
    }
  }

  /**
   * Save appointments to IndexedDB
   * @param appointments The appointments to save
   */
  async saveAppointments(appointments: Appointment[]): Promise<void> {
    try {
      console.log(`Saving ${appointments.length} appointments to IndexedDB`);
      
      // Process appointments to ensure UTC time format
      const processedAppointments = appointments.map(appointment => 
        this.ensureAppointmentUTCTimeFormat(appointment)
      );
      
      // Log the structure of the first few appointments to understand what's coming from the API
      if (processedAppointments.length > 0) {
        console.log('Sample appointment structure:', {
          id: processedAppointments[0].id,
          title: processedAppointments[0].title,
          recurringAppointmentId: processedAppointments[0].recurringAppointmentId,
          recurrenceRule: processedAppointments[0].recurrenceRule,
          isException: processedAppointments[0].isException,
          exceptionDate: processedAppointments[0].exceptionDate,
          startTime: processedAppointments[0].startTime,
          endTime: processedAppointments[0].endTime,
          originalStartTime: processedAppointments[0].originalStartTime,
          originalEndTime: processedAppointments[0].originalEndTime
        });
      }
      
      // Find the base recurring appointment IDs and the actual base appointments
      const baseRecurringIds = new Set<string>();
      const baseAppointments = new Map<string, Appointment>();
      
      // First pass: identify all recurring appointment IDs
      processedAppointments.forEach(appointment => {
        if (appointment.recurringAppointmentId) {
          baseRecurringIds.add(appointment.recurringAppointmentId);
        }
      });
      
      // Second pass: find the base appointments that match these IDs
      processedAppointments.forEach(appointment => {
        if (baseRecurringIds.has(appointment.id)) {
          baseAppointments.set(appointment.id, appointment);
        }
      });
      
      console.log('Base recurring appointment IDs:', Array.from(baseRecurringIds));
      console.log('Found base appointments:', Array.from(baseAppointments.keys()));
      
      // If we didn't find any base appointments but have recurring IDs,
      // we need to create placeholder base appointments
      if (baseRecurringIds.size > 0 && baseAppointments.size === 0) {
        console.log('No base appointments found in API response, creating placeholders');
        
        // Group appointments by recurringAppointmentId
        const appointmentsByRecurringId = new Map<string, Appointment[]>();
        
        processedAppointments.forEach(appointment => {
          if (appointment.recurringAppointmentId) {
            if (!appointmentsByRecurringId.has(appointment.recurringAppointmentId)) {
              appointmentsByRecurringId.set(appointment.recurringAppointmentId, []);
            }
            appointmentsByRecurringId.get(appointment.recurringAppointmentId)!.push(appointment);
          }
        });
        
        // For each recurring ID, create a base appointment from the first instance
        appointmentsByRecurringId.forEach((instances, recurringId) => {
          if (instances.length > 0) {
            const firstInstance = instances[0];
            
            // Create a base appointment from the first instance
            // Use original dates if available, otherwise fall back to instance dates
            const baseAppointment: Appointment = {
              ...firstInstance,
              id: recurringId,
              recurringAppointmentId: undefined,
              isException: false,
              exceptionDate: undefined,
              isRecurringInstance: false,
              // Use original dates if available, otherwise use instance dates
              startTime: firstInstance.originalStartTime || firstInstance.startTime,
              endTime: firstInstance.originalEndTime || firstInstance.endTime
            };
            
            baseAppointments.set(recurringId, baseAppointment);
            console.log(`Created placeholder base appointment for ${recurringId}:`, {
              title: baseAppointment.title,
              startTime: baseAppointment.startTime,
              endTime: baseAppointment.endTime,
              originalStartTime: firstInstance.originalStartTime,
              originalEndTime: firstInstance.originalEndTime,
              instanceStartTime: firstInstance.startTime,
              instanceEndTime: firstInstance.endTime
            });
          }
        });
      }
      
      // Step 1: Clear existing appointments
      console.log('Clearing existing appointments from IndexedDB');
      await this.clearStore(STORES.APPOINTMENTS);
      console.log('Successfully cleared existing appointments from IndexedDB');
      
      // Step 2: Clear existing appointment exceptions
      console.log('Clearing existing appointment exceptions from IndexedDB');
      await this.clearStore(STORES.APPOINTMENT_EXCEPTIONS);
      console.log('Successfully cleared existing appointment exceptions from IndexedDB');
      
      // Step 3: Process exceptions
      const exceptions: AppointmentException[] = [];
      const exceptionIds = new Set<string>(); // Track which appointments are exceptions
      
      // Extract exceptions from processedAppointments
      for (const appointment of processedAppointments) {
        if (appointment.isException && appointment.recurringAppointmentId) {
          console.log(`Processing exception: ${appointment.id} for appointment ${appointment.recurringAppointmentId}`);
          
          try {
            // Generate an exceptionDate if it's null
            const exceptionDate = appointment.exceptionDate || 
                                  new Date(appointment.startTime).toISOString().split('T')[0];
            
            // Create an exception record
            const exception: AppointmentException = {
              id: `${appointment.recurringAppointmentId}_${exceptionDate}`,
              appointmentId: appointment.recurringAppointmentId,
              exceptionDate: exceptionDate,
              isDeleted: false,
              newStartTime: appointment.startTime,
              newEndTime: appointment.endTime,
              newTitle: appointment.title,
              newDescription: appointment.description,
              newAddress: appointment.address || '',
              newIsAllDay: appointment.isAllDay,
              createdAt: appointment.createdAt || new Date().toISOString(),
              updatedAt: appointment.updatedAt || new Date().toISOString()
            };
            
            exceptions.push(exception);
            exceptionIds.add(appointment.id); // Remember this is an exception
            console.log(`Created exception record: ${exception.id}`);
          } catch (error) {
            console.error(`Error creating exception for appointment ${appointment.id}:`, error);
          }
        }
      }
      
      // Step 4: Save the exceptions
      if (exceptions.length > 0) {
        console.log(`Saving ${exceptions.length} exceptions to IndexedDB`);
        await this.saveAppointmentExceptions(exceptions);
      }
      
      // Step 5: Save appointments (only base recurring and non-recurring appointments)
      console.log(`Processing ${processedAppointments.length} appointments - will only save base recurring and non-recurring appointments to IndexedDB`);
      console.log('Note: Recurring instances will be generated at runtime when appointments are requested');
      const store = await this.getStore(STORES.APPOINTMENTS, 'readwrite');
      
      // Prevent duplicates by storing what we've already added
      const processedIds = new Set<string>();
      let savedCount = 0;
      let skippedInstancesCount = 0;
      
      // First add all base appointments
      for (const [id, baseAppointment] of baseAppointments) {
        try {
          store.put(baseAppointment);
          processedIds.add(id);
          savedCount++;
          console.log(`Added base recurring appointment: ${baseAppointment.id}`);
        } catch (error) {
          console.error(`Error saving base appointment ${id}:`, error);
        }
      }
      
      // Then add the remaining appointments
      for (const appointment of processedAppointments) {
        // Skip if already processed or if it's an exception
        if (processedIds.has(appointment.id) || exceptionIds.has(appointment.id)) {
          continue;
        }
        
        // IMPORTANT: Skip recurring instances (appointments with recurringAppointmentId set)
        // We only store base/parent recurring appointments in IndexedDB
        // Recurring instances are generated at runtime when appointments are requested
        // This keeps the database smaller and ensures all instances have the correct up-to-date pattern
        // EXCEPTION: Store recurring instances that have recurrenceRule for offline functionality
        if (appointment.recurringAppointmentId && !appointment.recurrenceRule) {
          console.log(`Skipping recurring instance ${appointment.id} - instances are generated at runtime`);
          skippedInstancesCount++;
          continue;
        }
        
        // Store recurring instances that have recurrenceRule for offline functionality
        if (appointment.recurringAppointmentId && appointment.recurrenceRule) {
          console.log(`Storing recurring instance ${appointment.id} with recurrenceRule for offline functionality`);
        }
        
        // Log detailed info about the appointment
        console.log('Processing appointment:', {
          id: appointment.id,
          title: appointment.title,
          startTime: appointment.startTime,
          recurringId: appointment.recurringAppointmentId,
          recurrenceRule: appointment.recurrenceRule,
          isException: appointment.isException,
          isInstance: appointment.isRecurringInstance
        });
        
        try {
          store.put(appointment);
          processedIds.add(appointment.id);
          savedCount++;
        } catch (error) {
          console.error(`Error saving appointment ${appointment.id}:`, error);
        }
      }
      
      console.log(`Successfully saved ${savedCount} appointments to IndexedDB (skipped ${skippedInstancesCount} recurring instances)`);
    } catch (error) {
      console.error('Error saving appointments to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Get appointments from IndexedDB with optional filtering
   * @param startDate Optional start date filter
   * @param endDate Optional end date filter
   * @param appointmentTypeIds Optional appointment type IDs filter
   * @returns A promise that resolves to an array of filtered appointments
   */
  async getAppointments(
    startDate?: string, 
    endDate?: string, 
    appointmentTypeIds?: string[]
  ): Promise<Appointment[]> {
    try {
      const store = await this.getStore(STORES.APPOINTMENTS, 'readonly');
      
      return new Promise<Appointment[]>((resolve, reject) => {
        const request = store.getAll();
        
        request.onsuccess = () => {
          let appointments = request.result as Appointment[];
          
          // Process appointments to ensure UTC time format
          appointments = appointments.map(appointment => 
            this.ensureAppointmentUTCTimeFormat(appointment)
          );
          
          // Apply filters if provided - include appointments that overlap with the requested range
          if (startDate && endDate) {
            const startTimestamp = new Date(startDate).getTime();
            const endTimestamp = new Date(endDate).getTime();
            
            console.log(`[IndexedDB] Filtering appointments for range: ${startDate} to ${endDate}`);
            console.log(`[IndexedDB] Start timestamp: ${startTimestamp}, End timestamp: ${endTimestamp}`);
            
            const beforeFilter = appointments.length;
            appointments = appointments.filter(a => {
              const appointmentStart = new Date(a.startTime).getTime();
              const appointmentEnd = new Date(a.endTime).getTime();
              const overlaps = appointmentStart < endTimestamp && appointmentEnd > startTimestamp;
              
              if (!overlaps && a.description?.includes("Andrea away")) {
                console.log(`[IndexedDB] Filtering out appointment: ${a.title}`, {
                  appointmentStart: new Date(a.startTime).toISOString(),
                  appointmentEnd: new Date(a.endTime).toISOString(),
                  rangeStart: new Date(startDate).toISOString(),
                  rangeEnd: new Date(endDate).toISOString(),
                  appointmentStartTime: appointmentStart,
                  appointmentEndTime: appointmentEnd,
                  rangeStartTime: startTimestamp,
                  rangeEndTime: endTimestamp,
                  overlaps
                });
              }
              
              return overlaps;
            });
            
            console.log(`[IndexedDB] Filtered ${beforeFilter} appointments down to ${appointments.length} overlapping appointments`);
          } else if (startDate) {
            const startTimestamp = new Date(startDate).getTime();
            appointments = appointments.filter(a => 
              new Date(a.endTime).getTime() > startTimestamp
            );
          } else if (endDate) {
            const endTimestamp = new Date(endDate).getTime();
            appointments = appointments.filter(a => 
              new Date(a.startTime).getTime() < endTimestamp
            );
          }
          
          if (appointmentTypeIds && appointmentTypeIds.length > 0) {
            appointments = appointments.filter(a => 
              appointmentTypeIds.includes(a.appointmentTypeId)
            );
          }
          
          console.log(`Retrieved and processed ${appointments.length} appointments from IndexedDB`);
          resolve(appointments);
        };
        
        request.onerror = (event) => {
          console.error('Error getting appointments:', event);
          reject(new Error(`Failed to get appointments: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error('Error getting appointments from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Get a single appointment by ID
   * @param id The appointment ID
   * @returns A promise that resolves to the appointment or null if not found
   */
  async getAppointment(id: string): Promise<Appointment | null> {
    try {
      const store = await this.getStore(STORES.APPOINTMENTS, 'readonly');
      
      return new Promise<Appointment | null>((resolve, reject) => {
        const request = store.get(id);
        
        request.onsuccess = () => {
          const appointment = request.result as Appointment | undefined;
          
          if (appointment) {
            // Process the appointment to ensure proper UTC time format
            const processedAppointment = this.ensureAppointmentUTCTimeFormat(appointment);
            console.log(`Retrieved and processed appointment ${id} from IndexedDB:`, {
              title: processedAppointment.title,
              startTime: processedAppointment.startTime,
              endTime: processedAppointment.endTime,
              timeZoneId: processedAppointment.timeZoneId
            });
            resolve(processedAppointment);
          } else {
            console.log(`Appointment ${id} not found in IndexedDB`);
            resolve(null);
          }
        };
        
        request.onerror = (event) => {
          console.error(`Error getting appointment ${id}:`, event);
          reject(new Error(`Failed to get appointment: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error getting appointment ${id} from IndexedDB:`, error);
      return null;
    }
  }

  /**
   * Save appointment types to IndexedDB
   * @param appointmentTypes The appointment types to save
   */
  async saveAppointmentTypes(appointmentTypes: AppointmentType[]): Promise<void> {
    try {
      console.log(`Saving ${appointmentTypes.length} appointment types to IndexedDB`);
      
      // Get the store in readwrite mode
      const store = await this.getStore(STORES.APPOINTMENT_TYPES, 'readwrite');
      
      // Use put instead of add to handle existing records
      const promises = appointmentTypes.map(appointmentType => {
        return new Promise<void>((resolve, reject) => {
          try {
            // Use put instead of add to update existing records
            const request = store.put(appointmentType);
            
            request.onsuccess = () => {
              resolve();
            };
            
            request.onerror = (event) => {
              console.error('Error saving appointment type:', event);
              reject(new Error(`Failed to save appointment type: ${(event.target as IDBRequest).error}`));
            };
          } catch (error) {
            console.error('Exception while saving appointment type:', error);
            reject(error);
          }
        });
      });
      
      // Wait for all promises to resolve
      await Promise.all(promises);
      
      console.log(`Successfully saved ${appointmentTypes.length} appointment types to IndexedDB`);
    } catch (error) {
      console.error('Error saving appointment types to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Get appointment types from IndexedDB
   * @param businessId Optional business ID filter
   * @returns A promise that resolves to an array of appointment types
   */
  async getAppointmentTypes(businessId?: string): Promise<AppointmentType[]> {
    try {
      const store = await this.getStore(STORES.APPOINTMENT_TYPES, 'readonly');
      
      return new Promise<AppointmentType[]>((resolve, reject) => {
        const request = store.getAll();
        
        request.onsuccess = () => {
          let appointmentTypes = request.result as AppointmentType[];
          
          // Apply business filter if provided
          if (businessId) {
            appointmentTypes = appointmentTypes.filter(at => at.businessId === businessId);
          }
          
          console.log(`Retrieved ${appointmentTypes.length} appointment types from IndexedDB`);
          resolve(appointmentTypes);
        };
        
        request.onerror = (event) => {
          console.error('Error getting appointment types:', event);
          reject(new Error(`Failed to get appointment types: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error('Error getting appointment types from IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Save businesses to IndexedDB
   * @param businesses The businesses to save
   */
  async saveBusinesses(businesses: Business[]): Promise<void> {
    try {
      console.log(`Saving ${businesses.length} businesses to IndexedDB`);
      
      // Get the store in readwrite mode
      const store = await this.getStore(STORES.BUSINESSES, 'readwrite');
      
      // Use put instead of add to handle existing records
      const promises = businesses.map(business => {
        return new Promise<void>((resolve, reject) => {
          try {
            // Use put instead of add to update existing records
            const request = store.put(business);
            
            request.onsuccess = () => {
              resolve();
            };
            
            request.onerror = (event) => {
              console.error('Error saving business:', event);
              reject(new Error(`Failed to save business: ${(event.target as IDBRequest).error}`));
            };
          } catch (error) {
            console.error('Exception while saving business:', error);
            reject(error);
          }
        });
      });
      
      // Wait for all promises to resolve
      await Promise.all(promises);
      
      console.log(`Successfully saved ${businesses.length} businesses to IndexedDB`);
    } catch (error) {
      console.error('Error saving businesses to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Get businesses from IndexedDB
   * @returns A promise that resolves to an array of businesses
   */
  async getBusinesses(): Promise<Business[]> {
    try {
      const store = await this.getStore(STORES.BUSINESSES, 'readonly');
      
      return new Promise<Business[]>((resolve, reject) => {
        const request = store.getAll();
        
        request.onsuccess = () => {
          const businesses = request.result as Business[];
          console.log(`Retrieved ${businesses.length} businesses from IndexedDB`);
          resolve(businesses);
        };
        
        request.onerror = (event) => {
          console.error('Error getting businesses:', event);
          reject(new Error(`Failed to get businesses: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error('Error getting businesses from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Save business users to IndexedDB
   * @param users The business users to save
   */
  async saveBusinessUsers(users: BusinessUser[]): Promise<void> {
    try {
      console.log(`Saving ${users.length} business users to IndexedDB`);
      
      // Get the store in readwrite mode
      const store = await this.getStore(STORES.USERS, 'readwrite');
      
      // Use put instead of add to handle existing records
      const promises = users.map(user => {
        return new Promise<void>((resolve, reject) => {
          try {
            // Use put instead of add to update existing records
            const request = store.put(user);
            
            request.onsuccess = () => {
              resolve();
            };
            
            request.onerror = (event) => {
              console.error('Error saving business user:', event);
              reject(new Error(`Failed to save business user: ${(event.target as IDBRequest).error}`));
            };
          } catch (error) {
            console.error('Exception while saving business user:', error);
            reject(error);
          }
        });
      });
      
      // Wait for all promises to resolve
      await Promise.all(promises);
      
      console.log(`Successfully saved ${users.length} business users to IndexedDB`);
    } catch (error) {
      console.error('Error saving business users to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Get business users from IndexedDB
   * @param businessId Optional business ID filter
   * @returns A promise that resolves to an array of business users
   */
  async getBusinessUsers(businessId?: string): Promise<BusinessUser[]> {
    try {
      const store = await this.getStore(STORES.USERS, 'readonly');
      
      return new Promise<BusinessUser[]>((resolve, reject) => {
        const request = store.getAll();
        
        request.onsuccess = () => {
          let users = request.result as BusinessUser[];
          
          // Apply business filter if provided
          if (businessId) {
            users = users.filter(u => u.businessId === businessId);
          }
          
          console.log(`Retrieved ${users.length} business users from IndexedDB`);
          resolve(users);
        };
        
        request.onerror = (event) => {
          console.error('Error getting business users:', event);
          reject(new Error(`Failed to get business users: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error('Error getting business users from IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Add a pending change to be synced when online
   * @param change The change to add
   * @returns A promise that resolves to the ID of the added change
   */
  async addPendingChange(change: Omit<PendingChange, 'id' | 'timestamp'>): Promise<string> {
    try {
      const store = await this.getStore(STORES.PENDING_CHANGES, 'readwrite');
      
      const newChange: PendingChange = {
        ...change,
        id: crypto.randomUUID(),
        timestamp: Date.now()
      };
      
      return new Promise<string>((resolve, reject) => {
        const request = store.add(newChange);
        
        request.onsuccess = () => {
          console.log('Added pending change:', newChange);
          resolve(newChange.id);
        };
        
        request.onerror = (event) => {
          console.error('Error adding pending change:', event);
          reject(new Error(`Failed to add pending change: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error('Error adding pending change to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Get all pending changes
   * @returns A promise that resolves to an array of pending changes
   */
  async getPendingChanges(): Promise<PendingChange[]> {
    try {
      const store = await this.getStore(STORES.PENDING_CHANGES, 'readonly');
      
      return new Promise<PendingChange[]>((resolve, reject) => {
        const request = store.getAll();
        
        request.onsuccess = () => {
          const pendingChanges = request.result as PendingChange[];
          console.log(`Retrieved ${pendingChanges.length} pending changes from IndexedDB`);
          resolve(pendingChanges);
        };
        
        request.onerror = (event) => {
          console.error('Error getting pending changes:', event);
          reject(new Error(`Failed to get pending changes: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error('Error getting pending changes from IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Remove a pending change after it has been synced
   * @param id The ID of the change to remove
   */
  async removePendingChange(id: string): Promise<void> {
    try {
      const store = await this.getStore(STORES.PENDING_CHANGES, 'readwrite');
      
      return new Promise<void>((resolve, reject) => {
        const request = store.delete(id);
        
        request.onsuccess = () => {
          console.log(`Removed pending change ${id}`);
          resolve();
        };
        
        request.onerror = (event) => {
          console.error(`Error removing pending change ${id}:`, event);
          reject(new Error(`Failed to remove pending change ${id}: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error removing pending change ${id} from IndexedDB:`, error);
      throw error;
    }
  }

  /**
   * Set sync metadata
   * @param id The metadata ID
   * @param value The metadata value
   */
  async setSyncMetadata(id: string, value: any): Promise<void> {
    try {
      const store = await this.getStore(STORES.SYNC_METADATA, 'readwrite');
      
      return new Promise<void>((resolve, reject) => {
        const request = store.put({ id, value });
        
        request.onsuccess = () => {
          console.log(`Set sync metadata ${id} to ${JSON.stringify(value)}`);
          resolve();
        };
        
        request.onerror = (event) => {
          console.error(`Error setting sync metadata ${id}:`, event);
          reject(new Error(`Failed to set sync metadata ${id}: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error setting sync metadata ${id} in IndexedDB:`, error);
      throw error;
    }
  }

  /**
   * Get sync metadata
   * @param id The metadata ID
   * @returns A promise that resolves to the metadata value
   */
  async getSyncMetadata(id: string): Promise<any> {
    try {
      const store = await this.getStore(STORES.SYNC_METADATA, 'readonly');
      
      return new Promise<any>((resolve, reject) => {
        const request = store.get(id);
        
        request.onsuccess = () => {
          const result = request.result ? request.result.value : null;
          console.log(`Retrieved sync metadata ${id}: ${JSON.stringify(result)}`);
          resolve(result);
        };
        
        request.onerror = (event) => {
          console.error(`Error getting sync metadata ${id}:`, event);
          reject(new Error(`Failed to get sync metadata ${id}: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error getting sync metadata ${id} from IndexedDB:`, error);
      throw error;
    }
  }

  /**
   * Clear a store in IndexedDB
   * @param storeName The name of the store to clear
   */
  async clearStore(storeName: string): Promise<void> {
    try {
      const store = await this.getStore(storeName, 'readwrite');
      
      return new Promise<void>((resolve, reject) => {
        const request = store.clear();
        
        request.onsuccess = () => {
          console.log(`Cleared store: ${storeName}`);
          resolve();
        };
        
        request.onerror = (event) => {
          console.error(`Error clearing store ${storeName}:`, event);
          reject(new Error(`Failed to clear store ${storeName}: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error clearing store ${storeName}:`, error);
      throw error;
    }
  }

  /**
   * Update an appointment in IndexedDB
   * @param appointment The appointment to update
   */
  async updateAppointment(appointment: Appointment): Promise<void> {
    try {
      // Process the appointment to ensure proper UTC time format
      const processedAppointment = this.ensureAppointmentUTCTimeFormat(appointment);
      
      console.log(`Updating appointment ${processedAppointment.id} in IndexedDB:`, {
        title: processedAppointment.title,
        startTime: processedAppointment.startTime,
        endTime: processedAppointment.endTime,
        timeZoneId: processedAppointment.timeZoneId
      });
      
      const store = await this.getStore(STORES.APPOINTMENTS, 'readwrite');
      
      return new Promise<void>((resolve, reject) => {
        const request = store.put(processedAppointment);
        
        request.onsuccess = () => {
          console.log(`Updated appointment ${processedAppointment.id} in IndexedDB`);
          resolve();
        };
        
        request.onerror = (event) => {
          console.error(`Error updating appointment ${processedAppointment.id}:`, event);
          reject(new Error(`Failed to update appointment ${processedAppointment.id}: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error updating appointment ${appointment.id} in IndexedDB:`, error);
      throw error;
    }
  }

  /**
   * Delete an appointment from IndexedDB
   * @param id The ID of the appointment to delete
   */
  async deleteAppointment(id: string): Promise<void> {
    try {
      const store = await this.getStore(STORES.APPOINTMENTS, 'readwrite');
      
      return new Promise<void>((resolve, reject) => {
        const request = store.delete(id);
        
        request.onsuccess = () => {
          console.log(`Deleted appointment ${id} from IndexedDB`);
          resolve();
        };
        
        request.onerror = (event) => {
          console.error(`Error deleting appointment ${id}:`, event);
          reject(new Error(`Failed to delete appointment ${id}: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error deleting appointment ${id} from IndexedDB:`, error);
      throw error;
    }
  }

  /**
   * Get all appointments from IndexedDB without any filtering
   * @returns A promise that resolves to an array of all appointments
   */
  async getAllAppointments(): Promise<Appointment[]> {
    try {
      const store = await this.getStore(STORES.APPOINTMENTS, 'readonly');
      
      return new Promise<Appointment[]>((resolve, reject) => {
        const request = store.getAll();
        
        request.onsuccess = () => {
          const appointments = request.result as Appointment[];
          console.log(`Retrieved ${appointments.length} appointments from IndexedDB`);
          
          // Process each appointment to ensure proper UTC time format
          const processedAppointments = appointments.map(appointment => 
            this.ensureAppointmentUTCTimeFormat(appointment)
          );
          
          console.log(`Processed ${processedAppointments.length} appointments with proper UTC format`);
          resolve(processedAppointments);
        };
        
        request.onerror = (event) => {
          console.error('Error getting all appointments:', event);
          reject(new Error(`Failed to get all appointments: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error('Error getting all appointments from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Ensure all time fields in an AppointmentException have the 'Z' UTC suffix
   * @param exception The appointment exception to process
   * @returns The processed appointment exception with proper UTC time format
   */
  private ensureUTCTimeFormat(exception: AppointmentException): AppointmentException {
    const processed = { ...exception };
    
    // Add Z suffix to newStartTime if it exists and doesn't already have it
    if (processed.newStartTime && !processed.newStartTime.endsWith('Z')) {
      processed.newStartTime = `${processed.newStartTime}Z`;
      console.log(`Added Z suffix to exception newStartTime: ${processed.newStartTime}`);
    }
    
    // Add Z suffix to newEndTime if it exists and doesn't already have it
    if (processed.newEndTime && !processed.newEndTime.endsWith('Z')) {
      processed.newEndTime = `${processed.newEndTime}Z`;
      console.log(`Added Z suffix to exception newEndTime: ${processed.newEndTime}`);
    }
    
    // Also ensure createdAt and updatedAt have proper UTC format if they exist
    if (processed.createdAt && !processed.createdAt.endsWith('Z')) {
      processed.createdAt = `${processed.createdAt}Z`;
    }
    
    if (processed.updatedAt && !processed.updatedAt.endsWith('Z')) {
      processed.updatedAt = `${processed.updatedAt}Z`;
    }
    
    return processed;
  }

  /**
   * Save appointment exceptions to IndexedDB
   * @param exceptions The appointment exceptions to save
   */
  async saveAppointmentExceptions(exceptions: AppointmentException[]): Promise<void> {
    try {
      console.log(`Saving ${exceptions.length} appointment exceptions to IndexedDB`);
      
      const store = await this.getStore(STORES.APPOINTMENT_EXCEPTIONS, 'readwrite');
      
      const promises = exceptions.map(exception => {
        // Process the exception to ensure proper UTC time format
        const processedException = exception;//this.ensureUTCTimeFormat(exception);

        console.log('Processing appointment exception:', {
          id: processedException.id,
          title: processedException.newTitle,
          startTime: processedException.newStartTime
        });
        
        return new Promise<void>((resolve, reject) => {
          try {
            const request = store.put(processedException);
            
            request.onsuccess = () => {
              resolve();
            };
            
            request.onerror = (event) => {
              console.error(`Error saving exception ${processedException.id}:`, event);
              reject(new Error(`Failed to save exception: ${(event.target as IDBRequest).error}`));
            };
          } catch (error) {
            console.error(`Exception while saving exception ${processedException.id}:`, error);
            reject(error);
          }
        });
      });
      
      await Promise.all(promises);
      console.log(`Successfully saved ${exceptions.length} appointment exceptions to IndexedDB`);
    } catch (error) {
      console.error('Error saving appointment exceptions to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Save a single appointment exception to IndexedDB
   * @param exception The appointment exception to save
   */
  async saveAppointmentException(exception: AppointmentException): Promise<void> {
    try {
      console.log(`Saving appointment exception ${exception.id} to IndexedDB`, {
        appointmentId: exception.appointmentId,
        exceptionDate: exception.exceptionDate,
        newStartTime: exception.newStartTime,
        newEndTime: exception.newEndTime
      });
      
      // Process the exception to ensure proper UTC time format
      const processedException = this.ensureUTCTimeFormat(exception);
      
      console.log(`Processed appointment exception:`, {
        appointmentId: processedException.appointmentId,
        exceptionDate: processedException.exceptionDate,
        newStartTime: processedException.newStartTime,
        newEndTime: processedException.newEndTime
      });
      
      const store = await this.getStore(STORES.APPOINTMENT_EXCEPTIONS, 'readwrite');
      
      return new Promise<void>((resolve, reject) => {
        const request = store.put(processedException);
        
        request.onsuccess = () => {
          console.log(`Saved appointment exception ${processedException.id} to IndexedDB`);
          resolve();
        };
        
        request.onerror = (event) => {
          console.error(`Error saving appointment exception ${processedException.id}:`, event);
          reject(new Error(`Failed to save appointment exception: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error saving appointment exception ${exception.id} to IndexedDB:`, error);
      throw error;
    }
  }

  /**
   * Get appointment exceptions for a specific appointment
   * @param appointmentId The ID of the appointment
   * @returns A promise that resolves to an array of appointment exceptions
   */
  async getAppointmentExceptions(appointmentId: string): Promise<AppointmentException[]> {
    try {
      console.log(`Getting exceptions for appointment ${appointmentId} from IndexedDB`);
      const store = await this.getStore(STORES.APPOINTMENT_EXCEPTIONS, 'readonly');
      const index = store.index('appointmentId');
      
      return new Promise<AppointmentException[]>((resolve, reject) => {
        const request = index.getAll(appointmentId);
        
        request.onsuccess = () => {
          const exceptions = request.result as AppointmentException[];
          console.log(`Retrieved ${exceptions.length} exceptions for appointment ${appointmentId} from IndexedDB`);
          
          // Process each exception to ensure proper UTC time format
          const processedExceptions = exceptions.map(exception => this.ensureUTCTimeFormat(exception));
          
          // Log each exception for debugging
          processedExceptions.forEach(exception => {
            console.log(`Exception details for ${exception.id}:`, {
              appointmentId: exception.appointmentId,
              exceptionDate: exception.exceptionDate,
              isDeleted: exception.isDeleted,
              newStartTime: exception.newStartTime,
              newEndTime: exception.newEndTime
            });
          });
          
          resolve(processedExceptions);
        };
        
        request.onerror = (event) => {
          console.error(`Error getting exceptions for appointment ${appointmentId}:`, event);
          reject(new Error(`Failed to get appointment exceptions: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error getting exceptions for appointment ${appointmentId}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific appointment exception by ID
   * @param id The ID of the appointment exception
   * @returns A promise that resolves to the appointment exception or null if not found
   */
  async getAppointmentException(id: string): Promise<AppointmentException | null> {
    try {
      const store = await this.getStore(STORES.APPOINTMENT_EXCEPTIONS, 'readonly');
      
      return new Promise<AppointmentException | null>((resolve, reject) => {
        const request = store.get(id);
        
        request.onsuccess = () => {
          const exception = request.result as AppointmentException | undefined;
          
          if (exception) {
            // Process the exception to ensure proper UTC time format
            const processedException = this.ensureUTCTimeFormat(exception);
            console.log(`Retrieved and processed exception ${id} from IndexedDB:`, {
              appointmentId: processedException.appointmentId,
              exceptionDate: processedException.exceptionDate,
              newStartTime: processedException.newStartTime,
              newEndTime: processedException.newEndTime
            });
            resolve(processedException);
          } else {
            console.log(`Exception ${id} not found in IndexedDB`);
            resolve(null);
          }
        };
        
        request.onerror = (event) => {
          console.error(`Error getting exception ${id}:`, event);
          reject(new Error(`Failed to get exception: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error getting exception ${id} from IndexedDB:`, error);
      return null;
    }
  }

  /**
   * Delete an appointment exception from IndexedDB
   * @param id The ID of the appointment exception to delete
   */
  async deleteAppointmentException(id: string): Promise<void> {
    try {
      console.log(`Deleting appointment exception ${id} from IndexedDB`);
      
      const store = await this.getStore(STORES.APPOINTMENT_EXCEPTIONS, 'readwrite');
      
      return new Promise<void>((resolve, reject) => {
        const request = store.delete(id);
        
        request.onsuccess = () => {
          console.log(`Deleted appointment exception ${id} from IndexedDB`);
          resolve();
        };
        
        request.onerror = (event) => {
          console.error(`Error deleting appointment exception ${id}:`, event);
          reject(new Error(`Failed to delete appointment exception: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error deleting appointment exception ${id} from IndexedDB:`, error);
      throw error;
    }
  }

  /**
   * Get the database instance
   */
  private async getDatabase(): Promise<IDBDatabase> {
    if (!this.db) {
      try {
        console.log('Database not initialized, initializing now...');
        this.db = await this.initDatabase();
        console.log('Database initialized successfully');
      } catch (error) {
        console.error('Error initializing database:', error);
        // Try to delete and recreate the database if initialization fails
        try {
          console.log('Attempting to delete and recreate the database...');
          await new Promise<void>((resolve, reject) => {
            const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
            deleteRequest.onsuccess = () => {
              console.log('Database deleted successfully');
              resolve();
            };
            deleteRequest.onerror = (event) => {
              console.error('Error deleting database:', event);
              reject(new Error('Could not delete database'));
            };
          });
          
          // Reinitialize the database
          this.dbPromise = null; // Reset the promise
          this.db = await this.initDatabase();
          console.log('Database recreated successfully');
        } catch (recreateError) {
          console.error('Error recreating database:', recreateError);
          throw new Error('Could not initialize database after recreation attempt');
        }
      }
    }
    
    // Verify that the database has all required object stores
    const requiredStores = Object.values(STORES);
    const missingStores = requiredStores.filter(store => !this.db!.objectStoreNames.contains(store));
    
    if (missingStores.length > 0) {
      console.error(`Database is missing required object stores: ${missingStores.join(', ')}`);
      
      // Close the current database connection
      this.db.close();
      this.db = null;
      this.dbPromise = null;
      
      // Increment the database version to force an upgrade
      const newVersion = DB_VERSION + 1;
      console.log(`Upgrading database to version ${newVersion} to create missing stores`);
      
      // Reinitialize with the new version
      const request = indexedDB.open(DB_NAME, newVersion);
      
      this.dbPromise = new Promise((resolve, reject) => {
        request.onerror = (event) => {
          console.error('Error opening database with new version:', event);
          reject(new Error('Could not open database with new version'));
        };
        
        request.onsuccess = (event) => {
          this.db = (event.target as IDBOpenDBRequest).result;
          console.log('Database opened successfully with new version');
          resolve(this.db);
        };
        
        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          
            // Create missing object stores
            for (const storeName of missingStores) {
              if (!db.objectStoreNames.contains(storeName)) {
                console.log(`Creating missing object store: ${storeName}`);
                
                if (storeName === STORES.APPOINTMENTS) {
                  const store = db.createObjectStore(storeName, { keyPath: 'id' });
                  store.createIndex('businessId', 'businessId', { unique: false });
                  store.createIndex('startTime', 'startTime', { unique: false });
                  store.createIndex('recurringAppointmentId', 'recurringAppointmentId', { unique: false });
                  store.createIndex('businessId_startTime', ['businessId', 'startTime'], { unique: false });
                } else if (storeName === STORES.APPOINTMENT_TYPES) {
                  const store = db.createObjectStore(storeName, { keyPath: 'id' });
                  store.createIndex('businessId', 'businessId', { unique: false });
                } else if (storeName === STORES.USERS) {
                  const store = db.createObjectStore(storeName, { keyPath: 'id' });
                  store.createIndex('businessId', 'businessId', { unique: false });
                } else if (storeName === STORES.PENDING_CHANGES) {
                  const store = db.createObjectStore(storeName, { keyPath: 'id' });
                  store.createIndex('entityType', 'entityType', { unique: false });
                  store.createIndex('timestamp', 'timestamp', { unique: false });
                } else if (storeName === STORES.APPOINTMENT_EXCEPTIONS) {
                  const store = db.createObjectStore(storeName, { keyPath: 'id' });
                  store.createIndex('appointmentId', 'appointmentId', { unique: false });
                  store.createIndex('exceptionDate', 'exceptionDate', { unique: false });
                } else if (storeName === STORES.CUSTOMERS) {
                  const store = db.createObjectStore(storeName, { keyPath: 'id' });
                  store.createIndex('businessId', 'businessId', { unique: false });
                } else if (storeName === STORES.JOBS) {
                  const store = db.createObjectStore(storeName, { keyPath: 'id' });
                  store.createIndex('businessId', 'businessId', { unique: false });
                  store.createIndex('customerId', 'customerId', { unique: false });
                } else {
                  db.createObjectStore(storeName, { keyPath: 'id' });
                }
              }
            }
        };
      });
      
      // Wait for the database to be initialized with the new version
      this.db = await this.dbPromise;
    }
    
    return this.db;
  }

  /**
   * Get all appointments for a specific business
   * @param businessId The business ID
   * @returns A promise that resolves to an array of appointments
   */
  async getAppointmentsForBusiness(businessId: string): Promise<Appointment[]> {
    try {
      const store = await this.getStore(STORES.APPOINTMENTS, 'readonly');
      const index = store.index('businessId');
      
      return new Promise<Appointment[]>((resolve, reject) => {
        const request = index.getAll(businessId);
        
        request.onsuccess = () => {
          const appointments = request.result as Appointment[];
          
          // Process appointments to ensure UTC time format
          const processedAppointments = appointments.map(appointment => 
            this.ensureAppointmentUTCTimeFormat(appointment)
          );
          
          console.log(`Retrieved and processed ${processedAppointments.length} appointments for business ${businessId} from IndexedDB`);
          resolve(processedAppointments);
        };
        
        request.onerror = (event) => {
          console.error(`Error getting appointments for business ${businessId}:`, event);
          reject(new Error(`Failed to get appointments: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error getting appointments for business ${businessId} from IndexedDB:`, error);
      return [];
    }
  }

  /**
   * Get the current user from IndexedDB
   * @returns A promise that resolves to the current user or null if not found
   */
  async getCurrentUser(): Promise<any | null> {
    try {
      // First, try to get the current user ID from localStorage
      const currentUserId = localStorage.getItem('currentUserId');
      
      if (!currentUserId) {
        console.log('No current user ID found in localStorage, trying to find a user in IndexedDB');
        
        // If no current user ID, try to get the first user from the users store
        const store = await this.getStore(STORES.USERS, 'readonly');
        
        return new Promise<any | null>((resolve, reject) => {
          const request = store.getAll();
          
          request.onsuccess = () => {
            const users = request.result;
            if (users && users.length > 0) {
              console.log('Using first user as current user:', users[0]);
              
              // Save this user ID to localStorage for future use
              try {
                localStorage.setItem('currentUserId', users[0].id);
                console.log(`Saved user ID ${users[0].id} to localStorage`);
              } catch (storageError) {
                console.warn('Could not save user ID to localStorage:', storageError);
              }
              
              resolve(users[0]);
            } else {
              console.log('No users found in IndexedDB, checking businesses');
              
              // If no users, try to get the first business
              this.getStore(STORES.BUSINESSES, 'readonly')
                .then(businessStore => {
                  const businessRequest = businessStore.getAll();
                  
                  businessRequest.onsuccess = () => {
                    const businesses = businessRequest.result;
                    if (businesses && businesses.length > 0) {
                      console.log('Using first business:', businesses[0]);
                      resolve({
                        id: 'default-user',
                        businessId: businesses[0].id,
                        name: 'Default User',
                        email: 'default@example.com'
                      });
                    } else {
                      console.log('No businesses found in IndexedDB');
                      resolve(null);
                    }
                  };
                  
                  businessRequest.onerror = (event) => {
                    console.error('Error getting businesses:', event);
                    resolve(null);
                  };
                })
                .catch(error => {
                  console.error('Error getting business store:', error);
                  resolve(null);
                });
            }
          };
          
          request.onerror = (event) => {
            console.error('Error getting users:', event);
            reject(new Error(`Failed to get users: ${(event.target as IDBRequest).error}`));
          };
        });
      }
      
      // If we have a current user ID, get that specific user
      const store = await this.getStore(STORES.USERS, 'readonly');
      
      return new Promise<any | null>((resolve, reject) => {
        const request = store.get(currentUserId);
        
        request.onsuccess = () => {
          const user = request.result || null;
          if (user) {
            console.log(`Retrieved current user from IndexedDB: ${user.name || user.id}`);
            resolve(user);
          } else {
            console.log(`User with ID ${currentUserId} not found in IndexedDB, clearing localStorage and trying again`);
            localStorage.removeItem('currentUserId');
            // Recursively call getCurrentUser to try the fallback method
            this.getCurrentUser().then(resolve).catch(reject);
          }
        };
        
        request.onerror = (event) => {
          console.error(`Error getting current user:`, event);
          reject(new Error(`Failed to get current user: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error('Error getting current user from IndexedDB:', error);
      return null;
    }
  }

  /**
   * Get all appointment exceptions from IndexedDB
   * @returns A promise that resolves to an array of all appointment exceptions
   */
  async getAllAppointmentExceptions(): Promise<AppointmentException[]> {
    try {
      const store = await this.getStore(STORES.APPOINTMENT_EXCEPTIONS, 'readonly');
      
      return new Promise<AppointmentException[]>((resolve, reject) => {
        const request = store.getAll();
        
        request.onsuccess = () => {
          const exceptions = request.result as AppointmentException[];
          console.log(`Retrieved ${exceptions.length} appointment exceptions from IndexedDB`);
          
          // Process each exception to ensure proper UTC time format
          const processedExceptions = exceptions.map(exception => this.ensureUTCTimeFormat(exception));
          
          console.log(`Processed ${processedExceptions.length} appointment exceptions with proper UTC format`);
          resolve(processedExceptions);
        };
        
        request.onerror = (event) => {
          console.error('Error getting all appointment exceptions:', event);
          reject(new Error(`Failed to get appointment exceptions: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error('Error getting all appointment exceptions from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Ensure all time fields in an Appointment have the 'Z' UTC suffix
   * @param appointment The appointment to process
   * @returns The processed appointment with proper UTC time format
   */
  private ensureAppointmentUTCTimeFormat(appointment: Appointment): Appointment {
    const processed = { ...appointment };
    
    // Only add Z suffix if the time is already in UTC format but missing the suffix
    // This prevents incorrect timezone conversion
    if (processed.startTime && !processed.startTime.endsWith('Z')) {
      // Check if the time is already in UTC format (has timezone offset or is clearly UTC)
      const startTimeDate = new Date(processed.startTime);
      if (!isNaN(startTimeDate.getTime())) {
        // Only add Z if the time appears to be in UTC format (no timezone info)
        // and the parsed date matches the original string (indicating it's already UTC)
        const isoString = startTimeDate.toISOString();
        if (isoString === processed.startTime + 'Z') {
          processed.startTime = `${processed.startTime}Z`;
          console.log(`Added Z suffix to appointment startTime: ${processed.startTime}`);
        } else {
          console.log(`Preserving original startTime format: ${processed.startTime}`);
        }
      }
    }
    
    if (processed.endTime && !processed.endTime.endsWith('Z')) {
      const endTimeDate = new Date(processed.endTime);
      if (!isNaN(endTimeDate.getTime())) {
        const isoString = endTimeDate.toISOString();
        if (isoString === processed.endTime + 'Z') {
          processed.endTime = `${processed.endTime}Z`;
          console.log(`Added Z suffix to appointment endTime: ${processed.endTime}`);
        } else {
          console.log(`Preserving original endTime format: ${processed.endTime}`);
        }
      }
    }
    
    // Also ensure createdAt and updatedAt have proper UTC format if they exist
    if (processed.createdAt && !processed.createdAt.endsWith('Z')) {
      const createdAtDate = new Date(processed.createdAt);
      if (!isNaN(createdAtDate.getTime())) {
        const isoString = createdAtDate.toISOString();
        if (isoString === processed.createdAt + 'Z') {
          processed.createdAt = `${processed.createdAt}Z`;
        }
      }
    }
    
    if (processed.updatedAt && !processed.updatedAt.endsWith('Z')) {
      const updatedAtDate = new Date(processed.updatedAt);
      if (!isNaN(updatedAtDate.getTime())) {
        const isoString = updatedAtDate.toISOString();
        if (isoString === processed.updatedAt + 'Z') {
          processed.updatedAt = `${processed.updatedAt}Z`;
        }
      }
    }
    
    // Process original start and end times for recurring appointments
    if (processed.originalStartTime && !processed.originalStartTime.endsWith('Z')) {
      const originalStartTimeDate = new Date(processed.originalStartTime);
      if (!isNaN(originalStartTimeDate.getTime())) {
        const isoString = originalStartTimeDate.toISOString();
        if (isoString === processed.originalStartTime + 'Z') {
          processed.originalStartTime = `${processed.originalStartTime}Z`;
          console.log(`Added Z suffix to appointment originalStartTime: ${processed.originalStartTime}`);
        } else {
          console.log(`Preserving original originalStartTime format: ${processed.originalStartTime}`);
        }
      }
    }
    
    if (processed.originalEndTime && !processed.originalEndTime.endsWith('Z')) {
      const originalEndTimeDate = new Date(processed.originalEndTime);
      if (!isNaN(originalEndTimeDate.getTime())) {
        const isoString = originalEndTimeDate.toISOString();
        if (isoString === processed.originalEndTime + 'Z') {
          processed.originalEndTime = `${processed.originalEndTime}Z`;
          console.log(`Added Z suffix to appointment originalEndTime: ${processed.originalEndTime}`);
        } else {
          console.log(`Preserving original originalEndTime format: ${processed.originalEndTime}`);
        }
      }
    }
    
    return processed;
  }

  // ==================== CACHE-ORIENTED METHODS ====================
  
  /**
   * MIGRATION: Fix appointments with undefined businessId
   * This is a one-time fix for appointments saved before the cache update
   */
  async fixAppointmentsWithoutBusinessId(currentBusinessId: string): Promise<void> {
    try {
      console.log('Checking for appointments with undefined businessId...');
      
      const store = await this.getStore(STORES.APPOINTMENTS, 'readwrite');
      const allAppointments = await new Promise<Appointment[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result as Appointment[]);
        request.onerror = () => reject();
      });
      
      const appointmentsNeedingFix = allAppointments.filter(a => !a.businessId);
      
      if (appointmentsNeedingFix.length > 0) {
        console.log(`Found ${appointmentsNeedingFix.length} appointments with undefined businessId. Fixing...`);
        
        for (const appointment of appointmentsNeedingFix) {
          const fixed = { ...appointment, businessId: currentBusinessId };
          await new Promise<void>((resolve, reject) => {
            const putRequest = store.put(fixed);
            putRequest.onsuccess = () => resolve();
            putRequest.onerror = () => reject();
          });
        }
        
        console.log(`Fixed ${appointmentsNeedingFix.length} appointments with missing businessId`);
      } else {
        console.log('No appointments need businessId fix');
      }
    } catch (error) {
      console.error('Error fixing appointments with undefined businessId:', error);
    }
  }
  
  /**
   * Get cached appointments for a specific date range and business
   * Uses compound index for efficient queries
   */
  async getCachedAppointmentsForRange(start: Date, end: Date, businessId: string): Promise<Appointment[]> {
    try {
      const store = await this.getStore(STORES.APPOINTMENTS, 'readonly');
      
      // Try to use compound index if available, otherwise fall back to businessId index
      let index: IDBIndex;
      try {
        index = store.index('businessId_startTime');
        console.log('Using compound index for efficient range query');
        
        // Use compound index for range query
        const range = IDBKeyRange.bound(
          [businessId, start.toISOString()],
          [businessId, end.toISOString()]
        );
        
        return new Promise<Appointment[]>((resolve, reject) => {
          const request = index.getAll(range);
          
          request.onsuccess = () => {
            const appointments = (request.result as Appointment[]).map(a => 
              this.ensureAppointmentUTCTimeFormat(a)
            );
            console.log(`Retrieved ${appointments.length} cached appointments for range using compound index`);
            resolve(appointments);
          };
          
          request.onerror = (event) => {
            console.error('Error getting cached appointments:', event);
            reject(new Error(`Failed to get cached appointments: ${(event.target as IDBRequest).error}`));
          };
        });
      } catch (indexError) {
        // Compound index doesn't exist yet, fall back to getting all and filtering
        console.log('Compound index not available, getting all appointments and filtering');
        
        return new Promise<Appointment[]>((resolve, reject) => {
          const request = store.getAll();
          
          request.onsuccess = () => {
            let appointments = request.result as Appointment[];
            
            console.log(`Retrieved ${appointments.length} total appointments from IndexedDB`);
            
            // Filter by businessId (including undefined as a fallback for migration)
            const startTime = start.getTime();
            const endTime = end.getTime();
            
            appointments = appointments.filter(a => {
              const aptStart = new Date(a.startTime).getTime();
              const inDateRange = aptStart >= startTime && aptStart <= endTime;
              
              // Match businessId OR if appointment has undefined businessId (migration case)
              const matchesBusiness = a.businessId === businessId || !a.businessId;
              
              return inDateRange && matchesBusiness;
            });
            
            // Fix appointments with undefined businessId as we retrieve them
            appointments = appointments.map(a => {
              const processed = this.ensureAppointmentUTCTimeFormat(a);
              if (!processed.businessId) {
                console.log(`Fixing appointment ${processed.id} with undefined businessId`);
                processed.businessId = businessId;
                // Update in database asynchronously (don't wait)
                store.put(processed);
              }
              return processed;
            });
            
            console.log(`Retrieved ${appointments.length} cached appointments for range (with migration fix)`);
            resolve(appointments);
          };
          
          request.onerror = (event) => {
            console.error('Error getting cached appointments:', event);
            reject(new Error(`Failed to get cached appointments: ${(event.target as IDBRequest).error}`));
          };
        });
      }
    } catch (error) {
      console.error('Error getting cached appointments:', error);
      return [];
    }
  }

  /**
   * Merge fresh appointments from API with cached appointments
   * Preserves appointments outside the fetched range
   */
  async mergeCachedAppointments(
    freshAppointments: Appointment[],
    businessId: string,
    fetchedRange: { start: Date; end: Date }
  ): Promise<void> {
    try {
      console.log(`Merging ${freshAppointments.length} fresh appointments for business ${businessId}`);
      
      const store = await this.getStore(STORES.APPOINTMENTS, 'readwrite');
      const index = store.index('businessId');
      
      // Get all existing appointments for this business
      const existingAppointments = await new Promise<Appointment[]>((resolve, reject) => {
        const request = index.getAll(businessId);
        
        request.onsuccess = () => {
          resolve(request.result as Appointment[]);
        };
        
        request.onerror = (event) => {
          console.error('Error getting existing appointments:', event);
          reject(new Error(`Failed to get existing appointments: ${(event.target as IDBRequest).error}`));
        };
      });
      
      console.log(`Found ${existingAppointments.length} existing appointments in cache`);
      
      // Identify appointments in the fetched range that need to be removed
      const startTime = fetchedRange.start.getTime();
      const endTime = fetchedRange.end.getTime();
      
      // Don't remove recurring instances - they should be preserved
      const appointmentsToRemove = existingAppointments.filter(a => {
        const aptStart = new Date(a.startTime).getTime();
        const isInRange = aptStart >= startTime && aptStart <= endTime;
        const isRecurringInstance = a.recurringAppointmentId && a.id !== a.recurringAppointmentId;
        
        // Only remove non-recurring appointments in the range
        return isInRange && !isRecurringInstance;
      });
      
      console.log(`Removing ${appointmentsToRemove.length} non-recurring appointments in fetched range`);
      
      // Remove old non-recurring appointments in the fetched range
      for (const appointment of appointmentsToRemove) {
        await new Promise<void>((resolve, reject) => {
          const deleteRequest = store.delete(appointment.id);
          deleteRequest.onsuccess = () => resolve();
          deleteRequest.onerror = () => reject();
        });
      }
      
      // Process fresh appointments - ensure they have businessId set
      const processedAppointments = freshAppointments.map(a => {
        const processed = this.ensureAppointmentUTCTimeFormat(a);
        // CRITICAL: Ensure businessId is set (API might not return it)
        if (!processed.businessId) {
          console.log(`Adding missing businessId to appointment ${processed.id}`);
          processed.businessId = businessId;
        }
        return processed;
      });
      
      // Add fresh appointments
      for (const appointment of processedAppointments) {
        await new Promise<void>((resolve, reject) => {
          const putRequest = store.put(appointment);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = (event) => {
            console.error(`Error adding appointment ${appointment.id}:`, event);
            reject();
          };
        });
      }
      
      console.log(`Successfully merged appointments. Removed ${appointmentsToRemove.length}, added ${freshAppointments.length}`);
    } catch (error) {
      console.error('Error merging cached appointments:', error);
      throw error;
    }
  }

  /**
   * Add or update a single cached appointment
   */
  async addOrUpdateCachedAppointment(appointment: Appointment, businessId: string): Promise<void> {
    try {
      const processedAppointment = this.ensureAppointmentUTCTimeFormat(appointment);
      // CRITICAL: Ensure businessId is set (API might not return it)
      if (!processedAppointment.businessId) {
        console.log(`Adding missing businessId to appointment ${processedAppointment.id}`);
        processedAppointment.businessId = businessId;
      }
      await this.updateAppointment(processedAppointment);
      console.log(`Added/updated cached appointment ${appointment.id}`);
    } catch (error) {
      console.error(`Error adding/updating cached appointment ${appointment.id}:`, error);
      throw error;
    }
  }

  /**
   * Remove a cached appointment
   */
  async removeCachedAppointment(appointmentId: string, businessId: string): Promise<void> {
    try {
      await this.deleteAppointment(appointmentId);
      console.log(`Removed cached appointment ${appointmentId}`);
    } catch (error) {
      console.error(`Error removing cached appointment ${appointmentId}:`, error);
      throw error;
    }
  }

  /**
   * Get cache metadata for range tracking
   */
  async getCacheMetadata(): Promise<any> {
    return await this.getSyncMetadata('calendar_ranges');
  }

  /**
   * Update cache metadata
   */
  async updateCacheMetadata(metadata: any): Promise<void> {
    const existing = await this.getCacheMetadata();
    const updated = { ...existing, ...metadata };
    await this.setSyncMetadata('calendar_ranges', updated);
  }

  /**
   * Mark a date range as fetched
   */
  async markRangeFetched(start: Date, end: Date, businessId: string): Promise<void> {
    try {
      const metadata = await this.getCacheMetadata() || { cachedRanges: [] };
      
      const newRange = {
        start: start.toISOString(),
        end: end.toISOString(),
        fetchedAt: new Date().toISOString(),
        businessId
      };
      
      const ranges = metadata.cachedRanges || [];
      ranges.push(newRange);
      
      // Keep only last 20 ranges to avoid bloat
      const limitedRanges = ranges.slice(-20);
      
      await this.setSyncMetadata('calendar_ranges', {
        ...metadata,
        cachedRanges: limitedRanges
      });
      
      console.log(`Marked range as fetched: ${start.toISOString()} to ${end.toISOString()}`);
    } catch (error) {
      console.error('Error marking range as fetched:', error);
    }
  }

  /**
   * Check if a range has been cached
   */
  async hasRangeCached(start: Date, end: Date, businessId: string): Promise<boolean> {
    try {
      const metadata = await this.getCacheMetadata();
      if (!metadata || !metadata.cachedRanges) return false;
      
      return metadata.cachedRanges.some((range: any) => {
        if (range.businessId !== businessId) return false;
        
        const rangeStart = new Date(range.start);
        const rangeEnd = new Date(range.end);
        
        return rangeStart <= end && rangeEnd >= start;
      });
    } catch (error) {
      console.error('Error checking if range is cached:', error);
      return false;
    }
  }

  /**
   * Get cached calendar filters
   */
  async getCachedFilters(businessId: string): Promise<any> {
    const filters = await this.getSyncMetadata('calendar_filters');
    if (filters && filters.businessId === businessId) {
      return filters;
    }
    return null;
  }

  /**
   * Set cached calendar filters
   */
  async setCachedFilters(filters: any, businessId: string): Promise<void> {
    await this.setSyncMetadata('calendar_filters', {
      ...filters,
      businessId,
      lastUsed: new Date().toISOString()
    });
  }

  /**
   * Prune old appointments beyond retention period
   */
  async pruneOldCachedAppointments(retentionMonths: number, businessId: string): Promise<void> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setMonth(cutoffDate.getMonth() - retentionMonths);
      
      const store = await this.getStore(STORES.APPOINTMENTS, 'readwrite');
      const index = store.index('businessId');
      
      const appointments = await new Promise<Appointment[]>((resolve, reject) => {
        const request = index.getAll(businessId);
        request.onsuccess = () => resolve(request.result as Appointment[]);
        request.onerror = () => reject();
      });
      
      const appointmentsToRemove = appointments.filter(a => {
        const aptDate = new Date(a.startTime);
        return aptDate < cutoffDate;
      });
      
      console.log(`Pruning ${appointmentsToRemove.length} old appointments`);
      
      for (const appointment of appointmentsToRemove) {
        await new Promise<void>((resolve, reject) => {
          const deleteRequest = store.delete(appointment.id);
          deleteRequest.onsuccess = () => resolve();
          deleteRequest.onerror = () => reject();
        });
      }
      
      console.log(`Successfully pruned ${appointmentsToRemove.length} old appointments`);
    } catch (error) {
      console.error('Error pruning old appointments:', error);
    }
  }

  /**
   * Clear appointment cache for a specific business
   */
  async clearAppointmentCache(businessId?: string): Promise<void> {
    try {
      if (businessId) {
        const store = await this.getStore(STORES.APPOINTMENTS, 'readwrite');
        const index = store.index('businessId');
        
        const appointments = await new Promise<Appointment[]>((resolve, reject) => {
          const request = index.getAll(businessId);
          request.onsuccess = () => resolve(request.result as Appointment[]);
          request.onerror = () => reject();
        });
        
        for (const appointment of appointments) {
          await new Promise<void>((resolve, reject) => {
            const deleteRequest = store.delete(appointment.id);
            deleteRequest.onsuccess = () => resolve();
            deleteRequest.onerror = () => reject();
          });
        }
        
        console.log(`Cleared ${appointments.length} appointments for business ${businessId}`);
      } else {
        await this.clearStore(STORES.APPOINTMENTS);
        console.log('Cleared all appointments from cache');
      }
    } catch (error) {
      console.error('Error clearing appointment cache:', error);
    }
  }

  /**
   * Get a customer by ID from IndexedDB
   * @param customerId The customer ID
   * @returns A promise that resolves to the customer or null if not found
   */
  async getCustomer(customerId: string): Promise<any | null> {
    try {
      const store = await this.getStore(STORES.CUSTOMERS, 'readonly');
      
      return new Promise<any | null>((resolve, reject) => {
        const request = store.get(customerId);
        
        request.onsuccess = () => {
          const customer = request.result;
          if (customer) {
            console.log(`Retrieved customer ${customerId} from IndexedDB`);
          } else {
            console.log(`Customer ${customerId} not found in IndexedDB`);
          }
          resolve(customer || null);
        };
        
        request.onerror = (event) => {
          console.error(`Error getting customer ${customerId}:`, event);
          reject(new Error(`Failed to get customer: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error getting customer ${customerId} from IndexedDB:`, error);
      return null;
    }
  }

  /**
   * Get all customers for a specific business from IndexedDB
   * @param businessId The business ID
   * @returns A promise that resolves to an array of customers
   */
  async getCustomersForBusiness(businessId: string): Promise<any[]> {
    try {
      const store = await this.getStore(STORES.CUSTOMERS, 'readonly');
      const index = store.index('businessId');
      
      return new Promise<any[]>((resolve, reject) => {
        const request = index.getAll(businessId);
        
        request.onsuccess = () => {
          const customers = request.result as any[];
          console.log(`Retrieved ${customers.length} customers for business ${businessId} from IndexedDB`);
          resolve(customers);
        };
        
        request.onerror = (event) => {
          console.error(`Error getting customers for business ${businessId}:`, event);
          reject(new Error(`Failed to get customers: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error getting customers for business ${businessId} from IndexedDB:`, error);
      return [];
    }
  }

  /**
   * Get a job by ID from IndexedDB
   * @param jobId The job ID
   * @returns A promise that resolves to the job or null if not found
   */
  async getJob(jobId: string): Promise<any | null> {
    try {
      const store = await this.getStore(STORES.JOBS, 'readonly');
      
      return new Promise<any | null>((resolve, reject) => {
        const request = store.get(jobId);
        
        request.onsuccess = () => {
          const job = request.result;
          if (job) {
            console.log(`Retrieved job ${jobId} from IndexedDB`);
          } else {
            console.log(`Job ${jobId} not found in IndexedDB`);
          }
          resolve(job || null);
        };
        
        request.onerror = (event) => {
          console.error(`Error getting job ${jobId}:`, event);
          reject(new Error(`Failed to get job: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error getting job ${jobId} from IndexedDB:`, error);
      return null;
    }
  }

  /**
   * Get all jobs for a specific business from IndexedDB
   * @param businessId The business ID
   * @returns A promise that resolves to an array of jobs
   */
  async getJobsForBusiness(businessId: string): Promise<any[]> {
    try {
      const store = await this.getStore(STORES.JOBS, 'readonly');
      
      return new Promise<any[]>((resolve, reject) => {
        // Since jobs don't have businessId field, get all jobs
        const request = store.getAll();
        
        request.onsuccess = () => {
          const allJobs = request.result as any[];
          console.log(`Retrieved ${allJobs.length} total jobs from IndexedDB`);
          
          // Filter jobs by businessId if it exists, otherwise return all jobs
          // Note: Jobs don't have businessId field, so we return all jobs for now
          // In a real multi-tenant scenario, jobs would need businessId field
          const jobs = allJobs.filter(job => {
            // If job has businessId field, filter by it
            if (job.businessId !== undefined) {
              return job.businessId === businessId;
            }
            // If no businessId field, include the job (backward compatibility)
            return true;
          });
          
          console.log(`Filtered to ${jobs.length} jobs for business ${businessId} from IndexedDB`);
          resolve(jobs);
        };
        
        request.onerror = (event) => {
          console.error(`Error getting jobs for business ${businessId}:`, event);
          reject(new Error(`Failed to get jobs: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error getting jobs for business ${businessId} from IndexedDB:`, error);
      return [];
    }
  }

  /**
   * Get jobs for a specific customer from IndexedDB
   * @param customerId The customer ID
   * @returns A promise that resolves to an array of jobs
   */
  async getJobsForCustomer(customerId: string): Promise<any[]> {
    try {
      const store = await this.getStore(STORES.JOBS, 'readonly');
      const index = store.index('customerId');
      
      return new Promise<any[]>((resolve, reject) => {
        const request = index.getAll(customerId);
        
        request.onsuccess = () => {
          const jobs = request.result as any[];
          console.log(`Retrieved ${jobs.length} jobs for customer ${customerId} from IndexedDB`);
          resolve(jobs);
        };
        
        request.onerror = (event) => {
          console.error(`Error getting jobs for customer ${customerId}:`, event);
          reject(new Error(`Failed to get jobs: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error getting jobs for customer ${customerId} from IndexedDB:`, error);
      return [];
    }
  }

  /**
   * Store a customer in IndexedDB
   * @param customer The customer data to store
   * @returns A promise that resolves when the customer is stored
   */
  async storeCustomer(customer: any): Promise<void> {
    try {
      const store = await this.getStore(STORES.CUSTOMERS, 'readwrite');
      
      return new Promise<void>((resolve, reject) => {
        const request = store.put(customer);
        
        request.onsuccess = () => {
          console.log(`Stored customer ${customer.id} in IndexedDB`);
          resolve();
        };
        
        request.onerror = (event) => {
          console.error(`Error storing customer ${customer.id}:`, event);
          reject(new Error(`Failed to store customer: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error storing customer ${customer.id} in IndexedDB:`, error);
      throw error;
    }
  }

  /**
   * Store multiple customers in IndexedDB
   * @param customers Array of customer data to store
   * @returns A promise that resolves when all customers are stored
   */
  async storeCustomers(customers: any[]): Promise<void> {
    try {
      const store = await this.getStore(STORES.CUSTOMERS, 'readwrite');
      
      for (const customer of customers) {
        await new Promise<void>((resolve, reject) => {
          const request = store.put(customer);
          
          request.onsuccess = () => {
            console.log(`Stored customer ${customer.id} in IndexedDB`);
            resolve();
          };
          
          request.onerror = (event) => {
            console.error(`Error storing customer ${customer.id}:`, event);
            reject(new Error(`Failed to store customer: ${(event.target as IDBRequest).error}`));
          };
        });
      }
      
      console.log(`Successfully stored ${customers.length} customers in IndexedDB`);
    } catch (error) {
      console.error(`Error storing customers in IndexedDB:`, error);
      throw error;
    }
  }

  /**
   * Store a job in IndexedDB
   * @param job The job data to store
   * @param businessId The business ID to associate with the job
   * @returns A promise that resolves when the job is stored
   */
  async storeJob(job: any, businessId?: string): Promise<void> {
    try {
      const store = await this.getStore(STORES.JOBS, 'readwrite');
      
      // Ensure job has businessId field for multi-tenant support
      const jobWithBusinessId = {
        ...job,
        businessId: businessId || job.businessId
      };
      
      return new Promise<void>((resolve, reject) => {
        const request = store.put(jobWithBusinessId);
        
        request.onsuccess = () => {
          console.log(`Stored job ${job.id} in IndexedDB with businessId: ${jobWithBusinessId.businessId}`);
          resolve();
        };
        
        request.onerror = (event) => {
          console.error(`Error storing job ${job.id}:`, event);
          reject(new Error(`Failed to store job: ${(event.target as IDBRequest).error}`));
        };
      });
    } catch (error) {
      console.error(`Error storing job ${job.id} in IndexedDB:`, error);
      throw error;
    }
  }

  /**
   * Store multiple jobs in IndexedDB
   * @param jobs Array of job data to store
   * @param businessId The business ID to associate with the jobs
   * @returns A promise that resolves when all jobs are stored
   */
  async storeJobs(jobs: any[], businessId?: string): Promise<void> {
    try {
      const store = await this.getStore(STORES.JOBS, 'readwrite');
      
      for (const job of jobs) {
        // Ensure job has businessId field for multi-tenant support
        const jobWithBusinessId = {
          ...job,
          businessId: businessId || job.businessId
        };
        
        await new Promise<void>((resolve, reject) => {
          const request = store.put(jobWithBusinessId);
          
          request.onsuccess = () => {
            console.log(`Stored job ${job.id} in IndexedDB with businessId: ${jobWithBusinessId.businessId}`);
            resolve();
          };
          
          request.onerror = (event) => {
            console.error(`Error storing job ${job.id}:`, event);
            reject(new Error(`Failed to store job: ${(event.target as IDBRequest).error}`));
          };
        });
      }
      
      console.log(`Successfully stored ${jobs.length} jobs in IndexedDB`);
    } catch (error) {
      console.error(`Error storing jobs in IndexedDB:`, error);
      throw error;
    }
  }

  // Form-related methods
  async getForms(): Promise<any[]> {
    try {
      const store = await this.getStore(STORES.FORMS);
      
      return new Promise<any[]>((resolve, reject) => {
        const request = store.getAll();
        
        request.onsuccess = () => {
          const forms = request.result as any[];
          resolve(forms);
        };
        
        request.onerror = () => {
          reject(new Error('Failed to get forms from IndexedDB'));
        };
      });
    } catch (error) {
      console.error('Error getting forms from IndexedDB:', error);
      throw error;
    }
  }

  async getForm(id: number): Promise<any | null> {
    try {
      const store = await this.getStore(STORES.FORMS);
      
      return new Promise<any | null>((resolve, reject) => {
        const request = store.get(id);
        
        request.onsuccess = () => {
          resolve(request.result || null);
        };
        
        request.onerror = () => {
          reject(new Error('Failed to get form from IndexedDB'));
        };
      });
    } catch (error) {
      console.error('Error getting form from IndexedDB:', error);
      throw error;
    }
  }

  async getFormResponses(formId: number, jobId?: string): Promise<any[]> {
    try {
      const store = await this.getStore(STORES.FORM_RESPONSES);
      const index = store.index('formId');
      
      return new Promise<any[]>((resolve, reject) => {
        const request = index.getAll(formId);
        
        request.onsuccess = () => {
          let responses = request.result as any[];
          
          if (jobId) {
            responses = responses.filter(response => response.jobId === jobId);
          }
          
          resolve(responses);
        };
        
        request.onerror = () => {
          reject(new Error('Failed to get form responses from IndexedDB'));
        };
      });
    } catch (error) {
      console.error('Error getting form responses from IndexedDB:', error);
      throw error;
    }
  }

  async createFormResponse(responseData: any): Promise<any> {
    try {
      const store = await this.getStore(STORES.FORM_RESPONSES, 'readwrite');
      const id = Date.now(); // Simple ID generation for offline
      const response = {
        id,
        ...responseData,
        submittedDate: new Date().toISOString(),
        isActive: true
      };
      
      return new Promise<any>((resolve, reject) => {
        const request = store.add(response);
        
        request.onsuccess = () => {
          resolve(response);
        };
        
        request.onerror = () => {
          reject(new Error('Failed to create form response in IndexedDB'));
        };
      });
    } catch (error) {
      console.error('Error creating form response in IndexedDB:', error);
      throw error;
    }
  }
}

export const indexedDbService = new IndexedDbService(); 